/** @jsxImportSource @emotion/react */
import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  EuiText,
  htmlIdGenerator,
} from "@inscopix/ideas-eui";
import { useRef, useState } from "react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import assert from "assert";
import { FieldTable } from "../fields/FieldTable";
import { FieldColumnName } from "../fields/FieldColumnName";
import { FieldColumnMulti } from "../fields/FieldColumnMulti";
import { FieldColumn } from "../fields/FieldColumn";

type Fields = {
  tableId: {
    value: string | undefined;
    isValid: boolean;
  };
  headerName: {
    value: string;
    isValid: boolean;
    isDirty: boolean;
  };
  leftColumnId: {
    value: string | undefined;
    isValid: boolean;
  };
  rightColumnId: {
    value: string | undefined;
    isValid: boolean;
  };
  columnIds: {
    value: string[];
    isValid: boolean;
  };
};

const INITIAL_FIELDS: Fields = {
  tableId: {
    value: undefined,
    isValid: false,
  },
  headerName: {
    value: "",
    isValid: false,
    isDirty: false,
  },
  leftColumnId: {
    value: undefined,
    isValid: false,
  },
  rightColumnId: {
    value: undefined,
    isValid: false,
  },
  columnIds: {
    value: [],
    isValid: false,
  },
};

interface ModalJoinDataTableProps {
  onBack: () => void;
  onClose: () => void;
}

/** Component that renders a modal for joining data and analysis tables */
export const ModalJoinTable = ({
  onBack,
  onClose,
}: ModalJoinDataTableProps) => {
  const selectedTableId = useDataTableContext((s) => s.selectedTableId);
  const tables = useDataTableContext((s) => s.tables);
  const joinDataTable = useDataTableContext((s) => s.joinDataTable);
  const joinAnalysisTable = useDataTableContext((s) => s.joinAnalysisTable);
  const headerNameFieldRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formId] = useState(htmlIdGenerator()());
  const [fields, setFields] = useState(INITIAL_FIELDS);

  /**
   * Shifts focus to the name field.
   *
   * This is tried multiple times because EuiSuperSelect (used by
   * FieldFileMetadatum) refocuses itself after onChange is called. I believe
   * this is a bug in EUI but I think this is the best option in the absence of
   * a long-term fix.
   */
  const focusNameField = () => {
    const timer = setInterval(() => {
      if (document.activeElement !== headerNameFieldRef.current) {
        headerNameFieldRef.current?.focus();
      } else {
        clearInterval(timer);
      }
    });
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      assert(selectedTableId !== undefined);
      const sourceTableId = fields.tableId.value;
      assert(sourceTableId !== undefined);
      const sourceTableKind = tables.find((table) => table.id === sourceTableId)
        ?.kind;
      assert(sourceTableKind !== undefined);
      const leftColumnId = fields.leftColumnId.value;
      assert(leftColumnId !== undefined);
      const rightColumnId = fields.rightColumnId.value;
      assert(rightColumnId !== undefined);
      const headerName = fields.headerName.value;
      assert(headerName !== undefined);
      const { error } =
        sourceTableKind === "data"
          ? await joinDataTable({
              tableId: selectedTableId,
              headerName,
              sourceTableId,
              leftColumnId,
              rightColumnId,
              sourceColumnIds: fields.columnIds.value,
            })
          : await joinAnalysisTable({
              tableId: selectedTableId,
              headerName,
              sourceTableId,
              leftColumnId,
              rightColumnId,
              sourceColumnIds: fields.columnIds.value,
            });
      assert(error === undefined);
      addUtilityToastSuccess("Data table joined");
    } catch (error) {
      addUtilityToastFailure("Failed to join data table");
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <>
      <EuiModalHeader>
        <EuiModalHeaderTitle component="h3">
          Join Data Table
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiForm
          id={formId}
          component="form"
          role="form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <EuiText size="s">
            <p>Select columns from an existing data or analysis table.</p>
          </EuiText>

          <EuiSpacer />

          <FieldTable
            filter={{
              includeTable: (table) => table.id !== selectedTableId,
            }}
            value={fields.tableId.value}
            onChange={(value) => {
              // Update state
              setFields((prevFields) => {
                const newFields = { ...prevFields };

                // Set new table ID
                newFields.tableId = {
                  value: value?.id,
                  isValid: value?.id !== undefined,
                };

                const selectedTable = tables.find(
                  (table) => table.id === selectedTableId,
                );

                const leftColumn = selectedTable?.columns.find(
                  (column) => column.is_id_column,
                );
                if (leftColumn !== undefined) {
                  newFields.leftColumnId = {
                    value: leftColumn.id,
                    isValid: true,
                  };
                } else {
                  newFields.leftColumnId = {
                    value: undefined,
                    isValid: false,
                  };
                }

                const sourceTable = tables.find(
                  (table) => table.id === value?.id,
                );
                // Auto-fill right column field
                const rightColumns = sourceTable?.columns.filter(
                  (column) =>
                    column.definition.kind === "identifier" &&
                    column.definition.table.id === selectedTableId,
                );
                if (rightColumns !== undefined && rightColumns.length === 1) {
                  newFields.rightColumnId = {
                    value: rightColumns[0].id,
                    isValid: true,
                  };
                } else {
                  newFields.rightColumnId = {
                    value: undefined,
                    isValid: false,
                  };
                }

                // Reset columns field
                newFields.columnIds = {
                  value: [],
                  isValid: false,
                };

                // Auto-fill name field if not previously edited
                if (value !== undefined && !fields.headerName.isDirty) {
                  newFields.headerName = {
                    value: value.name,
                    isValid: value.name !== "",
                    isDirty: false,
                  };
                }

                return newFields;
              });

              // Shift focus to name field
              focusNameField();
            }}
          />

          {fields.tableId.value !== undefined && (
            <>
              <FieldColumnName
                ref={headerNameFieldRef}
                label="Header name"
                placeholder=""
                value={fields.headerName.value}
                onChange={(field) => {
                  setFields((prevFields) => {
                    const newFields = { ...prevFields };
                    newFields.headerName = {
                      value: field.value,
                      isValid: field.isValid,
                      isDirty: true,
                    };
                    return newFields;
                  });
                }}
              />
              <EuiSpacer size="m" />
              <EuiFlexGroup alignItems="flexEnd" gutterSize="s">
                <EuiFlexItem>
                  <FieldColumn
                    filter={{
                      tableId: selectedTableId,
                    }}
                    label="Join on..."
                    helpText="Column from this data table"
                    value={fields.leftColumnId.value}
                    onChange={(value) => {
                      setFields((prevFields) => {
                        const newFields = { ...prevFields };
                        newFields.leftColumnId = {
                          value: value.id,
                          isValid: true,
                        };
                        return newFields;
                      });
                    }}
                  />
                </EuiFlexItem>

                <EuiFlexItem>
                  <FieldColumn
                    filter={{
                      tableId: fields.tableId.value,
                    }}
                    label=""
                    helpText="Column from the joined data table"
                    value={fields.rightColumnId.value}
                    onChange={(value) => {
                      setFields((prevFields) => {
                        const newFields = { ...prevFields };
                        newFields.rightColumnId = {
                          value: value.id,
                          isValid: true,
                        };
                        return newFields;
                      });
                    }}
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiSpacer size="m" />
            </>
          )}

          {fields.tableId.value !== undefined && (
            <FieldColumnMulti
              includeColumn={() => true}
              tableId={fields.tableId.value}
              value={fields.columnIds.value}
              onChange={(columnIds) => {
                setFields((prevFields) => {
                  const newFields = { ...prevFields };
                  newFields.columnIds = {
                    value: columnIds,
                    isValid: columnIds.length > 0,
                  };
                  return newFields;
                });
              }}
            />
          )}
        </EuiForm>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty iconType="arrowLeft" onClick={onBack}>
          Back
        </EuiButtonEmpty>
        <EuiButton
          type="submit"
          form={formId}
          isLoading={isLoading}
          fill
          disabled={Object.values(fields).some((f) => !f.isValid)}
        >
          Insert
        </EuiButton>
      </EuiModalFooter>
    </>
  );
};
